import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, forkJoin, of, Subscription, switchMap } from 'rxjs';
import { UserManagementService } from 'src/app/services/api/user-management/user-management.service';
import {
  AcademicRankModel,
  PositionModel,
  RoleModel,
  UserManagementModel,
  UserManagementOption,
  UserManagementPayload,
} from 'src/app/services/models/user-management/user-management.model';
import { SharedModule } from 'src/shared/shared.module';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { ErrorMessage } from 'src/utils/error-message';
import { LabelMode } from 'src/utils/label-mode.enum';
import { SetError } from 'src/utils/set-error.validator';

declare const bootstrap: any;

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};

interface RoleGroup {
  title: string;
  roles: RoleModel[];
}

export type UserSaveAction = 'create' | 'edit';

export interface UserSaveFailedEvent {
  action: UserSaveAction;
  message: string;
}

@Component({
  selector: 'app-form-add-user',
  imports: [CommonModule, TranslateModule, SharedModule, ReactiveFormsModule],
  templateUrl: './form-add-user.component.html',
  styleUrl: './form-add-user.component.scss',
})
export class FormAddUserComponent implements AfterViewInit, OnDestroy {
  @ViewChild('saveUserConfirmDialog')
  saveUserConfirmDialog!: ConfirmDialogComponent;

  @Output() saved = new EventEmitter<UserSaveAction>();
  @Output() saveFailed = new EventEmitter<UserSaveFailedEvent>();
  @Output() closed = new EventEmitter<void>();

  fb = inject(FormBuilder);
  userManagementService = inject(UserManagementService);
  cdr = inject(ChangeDetectorRef);

  isSaving = false;
  userId?: number;
  academicRanks: AcademicRankModel[] = [];
  positions: PositionModel[] = [];
  departments: UserManagementOption[] = [];
  roles: RoleModel[] = [];
  roleGroups: RoleGroup[] = [];
  allowedRoleIds = new Set<number>();
  LabelMode = LabelMode;
  errors: Partial<Record<string, string>> = {};

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    academicRankId: [null as number | null],
    fullName: ['', Validators.required],
    positionId: [null as number | null, Validators.required],
    departmentId: [null as number | null, Validators.required],
    roleIds: [[] as number[], Validators.required],
  });

  private positionSubscription?: Subscription;

  constructor() {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.valueChanges.subscribe(() => {
        this.clearResolvedError(key);
      });
    });
  }

  get isEditMode(): boolean {
    return this.userId != null;
  }

  ngAfterViewInit(): void {
    const modalEl = document.getElementById('addNewUser');

    modalEl?.addEventListener('hidden.bs.modal', () => {
      this.clearForm();
      this.closed.emit();
    });
  }

  ngOnDestroy(): void {
    this.positionSubscription?.unsubscribe();
  }

  openModal(userId?: number): void {
    this.clearForm();
    this.userId = userId;

    this.loadOptions(() => {
      if (userId != null) {
        this.loadUserDetail(userId, () => this.showModal());
        return;
      }

      this.showModal();
    });
  }

  isRoleChecked(roleId: number): boolean {
    return this.form.controls.roleIds.value?.includes(roleId) ?? false;
  }

  isRoleDisabled(roleId: number): boolean {
    return !this.allowedRoleIds.has(roleId);
  }

  onRoleChange(roleId: number, checked: boolean): void {
    if (this.isRoleDisabled(roleId)) {
      return;
    }

    const roleIds = this.form.controls.roleIds.value ?? [];
    const nextRoleIds = checked
      ? Array.from(new Set([...roleIds, roleId]))
      : roleIds.filter((id) => id !== roleId);

    this.form.controls.roleIds.setValue(nextRoleIds);
    this.form.controls.roleIds.markAsTouched();
  }

  onSave(): void {
    this.errors = {};

    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errors = this.mapErrorsToMessages(SetError(this.form));
      return;
    }

    this.saveUserConfirmDialog.open();
  }

  onConfirmSave(): void {
    this.errors = {};

    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errors = this.mapErrorsToMessages(SetError(this.form));
      this.saveUserConfirmDialog.close();
      return;
    }

    this.isSaving = true;
    const userId = this.userId;
    const action: UserSaveAction = userId == null ? 'create' : 'edit';
    const request = userId == null
      ? this.userManagementService.createUser(this.buildPayload())
      : this.userManagementService.updateUser(userId, this.buildPayload());

    request.subscribe({
      next: () => {
        this.saveUserConfirmDialog.close();
        this.saved.emit(action);
        this.closeModal();
      },
      error: (err) => {
        console.error(err);
        this.saveUserConfirmDialog.close();
        this.saveFailed.emit({
          action,
          message: this.getSaveErrorMessage(err, action),
        });
        this.isSaving = false;
      },
      complete: () => {
        this.isSaving = false;
      },
    });
  }

  clearForm(): void {
    this.userId = undefined;
    this.form.reset({
      email: '',
      academicRankId: null,
      fullName: '',
      positionId: null,
      departmentId: null,
      roleIds: [],
    });
    this.allowedRoleIds.clear();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.errors = {};
  }

  private loadOptions(afterLoad?: () => void): void {
    forkJoin({
      academicRanks: this.userManagementService.getAcademicRanks(),
      positions: this.userManagementService.getPositions(),
      departments: this.userManagementService.getDepartments(false),
      roles: this.userManagementService.getRoles(),
    }).subscribe({
      next: ({ academicRanks, positions, departments, roles }) => {
        this.academicRanks = academicRanks;
        this.positions = positions;
        this.departments = departments;
        this.roles = roles;
        this.roleGroups = this.buildRoleGroups(roles);
        this.listenPositionChange();
        this.cdr.detectChanges();
        afterLoad?.();
      },
      error: (err) => console.error(err),
    });
  }

  private loadUserDetail(userId: number, afterLoad?: () => void): void {
    this.userManagementService.getUserById(userId).subscribe({
      next: (user) => {
        this.patchFormValue(user);
        afterLoad?.();
      },
      error: (err) => console.error(err),
    });
  }

  private patchFormValue(user: UserManagementModel): void {
    this.form.patchValue({
      email: user.email,
      academicRankId: user.academic_rank_id,
      fullName: user.full_name,
      positionId: user.position_id,
      departmentId: user.department_id,
      roleIds: user.roles.map((role) => role.id),
    }, { emitEvent: false });

    this.loadAllowedRoles(user.position_id, false);
    this.cdr.detectChanges();
  }

  private buildPayload(): UserManagementPayload {
    const value = this.form.getRawValue();

    return {
      user: {
        full_name: value.fullName ?? '',
        email: value.email ?? '',
        academic_rank_id: value.academicRankId,
        position_id: value.positionId,
        department_id: value.departmentId,
        role_ids: value.roleIds ?? [],
      },
    };
  }

  private buildRoleGroups(roles: RoleModel[]): RoleGroup[] {
    const roleMap = new Map(roles.map((role) => [role.name, role]));

    return [
      {
        title: 'กลุ่มแอดมิน',
        roles: this.pickRoles(roleMap, ['admin']),
      },
      {
        title: 'กลุ่มเจ้าหน้าที่',
        roles: this.pickRoles(roleMap, [
          'head_of_education',
          'academic_officer',
          'human_resource',
          'finance_officer',
          'document_officer',
        ]),
      },
      {
        title: 'กลุ่มอาจารย์',
        roles: this.pickRoles(roleMap, [
          'teacher',
          'head_of_department',
          'vice_dean_administration',
          'vice_dean_academic',
        ]),
      },
    ];
  }

  private pickRoles(roleMap: Map<string, RoleModel>, names: string[]): RoleModel[] {
    return names.map((name) => roleMap.get(name)).filter((role): role is RoleModel => role != null);
  }

  private listenPositionChange(): void {
    this.positionSubscription?.unsubscribe();
    this.positionSubscription = this.form.controls.positionId.valueChanges
      .pipe(
        distinctUntilChanged(),
        switchMap((positionId) => {
          if (positionId == null) {
            return of(null);
          }

          return this.userManagementService.getDropdownOptions(positionId);
        })
      )
      .subscribe({
        next: (options) => {
          if (options == null) {
            this.allowedRoleIds.clear();
            this.form.controls.roleIds.setValue([]);
            return;
          }

          this.allowedRoleIds = new Set(this.getOptionRoleIds(options.roles));
          this.removeDisallowedRoles();
          this.applyDefaultRoleForPosition(this.form.controls.positionId.value);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error(err);
          this.allowedRoleIds.clear();
          this.form.controls.roleIds.setValue([]);
        },
      });
  }

  private loadAllowedRoles(positionId: number | null, applyDefaultRole = true): void {
    if (positionId == null) {
      this.allowedRoleIds.clear();
      this.form.controls.roleIds.setValue([]);
      return;
    }

    this.userManagementService.getDropdownOptions(positionId).subscribe({
      next: ({ roles }) => {
        this.allowedRoleIds = new Set(this.getOptionRoleIds(roles));
        this.removeDisallowedRoles();
        if (applyDefaultRole) {
          this.applyDefaultRoleForPosition(positionId);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error(err);
        this.allowedRoleIds.clear();
        this.form.controls.roleIds.setValue([]);
      },
    });
  }

  private getOptionRoleIds(roles: UserManagementOption[]): number[] {
    return roles
      .map((role) => Number(role.value ?? role.id))
      .filter((roleId) => Number.isFinite(roleId));
  }

  private removeDisallowedRoles(): void {
    const roleIds = this.form.controls.roleIds.value ?? [];
    const allowedRoleIds = roleIds.filter((roleId) => this.allowedRoleIds.has(roleId));

    if (allowedRoleIds.length !== roleIds.length) {
      this.form.controls.roleIds.setValue(allowedRoleIds);
    }
  }

  private applyDefaultRoleForPosition(positionId: number | null): void {
    const currentRoleIds = this.form.controls.roleIds.value ?? [];

    if (currentRoleIds.length > 0) {
      return;
    }

    const defaultRole = this.findDefaultRoleForPosition(positionId);

    if (defaultRole == null) {
      return;
    }

    this.form.controls.roleIds.setValue([defaultRole.id]);
    this.form.controls.roleIds.markAsTouched();
  }

  private findDefaultRoleForPosition(positionId: number | null): RoleModel | null {
    const allowedRoles = this.roles.filter((role) => this.allowedRoleIds.has(role.id));

    if (allowedRoles.length === 0) {
      return null;
    }

    const position = this.positions.find((item) => item.id === positionId);
    const positionCode = this.normalizeCode(position?.code);

    if (positionCode !== '') {
      const roleByCode = allowedRoles.find((role) => this.normalizeCode(role.name) === positionCode);

      if (roleByCode != null) {
        return roleByCode;
      }
    }

    const positionLabels = [
      position?.name,
      position?.name_th,
      position?.name_en,
    ].map((label) => this.normalizeLabel(label)).filter((label) => label !== '');

    const roleByLabel = allowedRoles.find((role) => {
      const roleLabels = [
        role.label,
        role.name,
      ].map((label) => this.normalizeLabel(label)).filter((label) => label !== '');

      return roleLabels.some((roleLabel) => positionLabels.includes(roleLabel));
    });

    if (roleByLabel != null) {
      return roleByLabel;
    }

    return allowedRoles.length === 1 ? allowedRoles[0] : null;
  }

  private normalizeCode(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  private normalizeLabel(value: unknown): string {
    return typeof value === 'string'
      ? value.trim().toLowerCase().replace(/\s+/g, '')
      : '';
  }

  private closeModal(): void {
    const modalElement = document.getElementById('addNewUser');

    if (modalElement != null) {
      bootstrap.Modal.getInstance(modalElement)?.hide();
    }
  }

  private mapErrorsToMessages(errors: Record<string, unknown>): Record<string, string> {
    const errorMessage = new ErrorMessage();

    return Object.keys(errors).reduce((result, key) => {
      result[key] = errorMessage.getErrorMessage(errors[key]);

      return result;
    }, {} as Record<string, string>);
  }

  private clearResolvedError(key: string): void {
    const control = this.form.get(key);

    if (control?.valid === true && this.errors[key] != null) {
      delete this.errors[key];
    }
  }

  private getSaveErrorMessage(error: unknown, action: UserSaveAction): string {
    const fallbackMessage = action === 'create'
      ? 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง'
      : 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';
    const responseError = (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
          .map((item) => item.trim());

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;

      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private showModal(): void {
    this.cdr.detectChanges();
    const modalEl = document.getElementById('addNewUser');

    if (modalEl != null) {
      bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
    }
  }
}
